import React from 'react'
import { RiCalendarLine, RiUserLine } from 'react-icons/ri'
import { FaRegBuilding, FaUsers, FaCrown, FaClipboardList, FaMapMarkerAlt } from 'react-icons/fa'
import type { Setor, Processo } from '../../../../models'
import { isEquipe as checkIsEquipe } from '../../../../models'
import { UserAvatar } from '../../../../components/UserAvatar'
import { format } from 'date-fns'

interface SetorViewCardProps {
  setor: Setor
  isEquipe?: boolean
  onNavigateToProcesso?: (processo: Processo) => void
}

export const SetorViewCard: React.FC<SetorViewCardProps> = ({
  setor,
  isEquipe = false,
  onNavigateToProcesso
}) => {
  const membros = setor.usuarios_ativos || setor.usuariosAtivos || setor.usuarios || []
  const totalMembros = membros.length

  return (
    <div className="p-4 sm:p-6">
      {/* Layout Responsivo - Grid Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Coluna Esquerda */}
        <div className="space-y-4 sm:space-y-5">
          {/* Informações Básicas */}
          <div className={`bg-gradient-to-br ${
            isEquipe 
              ? 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800'
              : 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800'
          } border rounded-lg p-3 sm:p-4`}>
            <div className="flex items-center gap-2 mb-3">
              {isEquipe ? (
                <FaUsers className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
              ) : (
                <FaRegBuilding className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
              )}
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">
                Informações do {isEquipe ? 'Equipe' : 'Setor'}
              </h3>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Nome</label>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100">{setor.nome_setor}</p>
                {checkIsEquipe(setor) && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
                    Equipe
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Localização do Setor */}
          {setor.cidade && (
            <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-3">
                <FaMapMarkerAlt className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">Localização</h3>
              </div>
              
              <div className="flex items-center gap-3 p-2 sm:p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800">
                <FaMapMarkerAlt className="text-blue-500 flex-shrink-0 w-5 h-5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {setor.cidade.name} - {setor.cidade.estado?.name}, {setor.cidade.estado?.pais?.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    Localização do setor
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Chefe do Setor */}
          <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <FaCrown className="w-4 h-4 text-yellow-500" />
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">Chefe do {isEquipe ? 'Equipe' : 'Setor'}</h3>
            </div>
            
            {setor.chefe ? (
              <div className="flex items-center gap-3 p-2 sm:p-3 rounded-lg bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-200 dark:border-yellow-800">
                <UserAvatar user={setor.chefe} size="md" className="flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {setor.chefe.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                    {setor.chefe.cargo_usuario}
                  </p>
                  {setor.chefe.email && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {setor.chefe.email}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-2 sm:p-3 rounded-lg bg-gray-50 dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600">
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic">Sem chefe definido</p>
              </div>
            )}
          </div>
        </div>

        {/* Coluna Direita */}
        <div className="space-y-4 sm:space-y-5">
          {/* Membros do Setor */}
          <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <RiUserLine className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">
                Membros ({totalMembros})
              </h3>
            </div>

            {totalMembros > 0 ? (
              <>
                {/* Grid de Avatares */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {membros.slice(0, 8).map((usuario) => (
                    <div
                      key={usuario.id}
                      className="group relative"
                      title={usuario.name}
                    >
                      <UserAvatar
                        user={usuario}
                        size="md"
                        className="border-2 border-white dark:border-neutral-800"
                      />
                    </div>
                  ))}
                  {totalMembros > 8 && (
                    <div className="w-10 h-10 bg-gray-100 dark:bg-neutral-700 border-2 border-white dark:border-neutral-800 rounded-full flex items-center justify-center">
                      <span className="text-xs text-gray-600 dark:text-gray-400 font-semibold">
                        +{totalMembros - 8}
                      </span>
                    </div>
                  )}
                </div>

                {/* Lista Compacta - responsiva */}
                <div className="space-y-1.5 max-h-48 sm:max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent">
                  {membros.map((usuario) => (
                    <div
                      key={usuario.id}
                      className="flex items-center gap-2 p-1.5 sm:p-2 rounded-lg bg-gray-50 dark:bg-neutral-700 hover:bg-gray-100 dark:hover:bg-neutral-600 transition-colors"
                    >
                      <UserAvatar user={usuario} size="sm" className="flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {usuario.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {usuario.cargo_usuario}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-2 sm:p-3 rounded-lg bg-gray-50 dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600">
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic">
                  Nenhum membro cadastrado
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Seções de largura total - abaixo das colunas */}
      <div className="space-y-4 sm:space-y-5">
        {/* Processos Vinculados */}
        {setor.processos && setor.processos.length > 0 ? (
          <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <FaClipboardList className="w-4 h-4 text-green-600 dark:text-green-400" />
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">
                Processos Vinculados ({setor.processos.length})
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {setor.processos.map((processo) => (
                onNavigateToProcesso ? (
                  <button
                    key={processo.id}
                    onClick={() => onNavigateToProcesso(processo)}
                    className="flex items-center gap-3 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 transition-all w-full text-left group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <FaClipboardList className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-green-600 dark:group-hover:text-green-400">
                        {processo.nome_processo}
                      </p>
                      {processo.descricao && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {processo.descricao.substring(0, 40)}...
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-green-500 dark:text-green-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      Ver
                    </span>
                  </button>
                ) : (
                  <div
                    key={processo.id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                  >
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <FaClipboardList className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {processo.nome_processo}
                      </p>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <FaClipboardList className="w-4 h-4" />
              <p className="text-xs sm:text-sm italic">Nenhum processo cadastrado para este {isEquipe ? 'equipe' : 'setor'}</p>
            </div>
          </div>
        )}

        {/* Informações de Auditoria */}
        <div className="bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <RiCalendarLine className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">Informações de Auditoria</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-gray-500 dark:text-gray-400 mb-0.5">ID do {isEquipe ? 'Equipe' : 'Setor'}</p>
              <p className="font-mono font-semibold text-gray-900 dark:text-gray-100">#{setor.id}</p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 mb-0.5">Status</p>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                Ativo
              </span>
            </div>
            {setor.created_at && (
              <div>
                <p className="text-gray-500 dark:text-gray-400 mb-0.5">Criado em</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                  {format(new Date(setor.created_at!), "dd/MM/yyyy")}
                </p>
              </div>
            )}
            {setor.updated_at && (
              <div>
                <p className="text-gray-500 dark:text-gray-400 mb-0.5">Atualizado em</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                  {format(new Date(setor.updated_at!), "dd/MM/yyyy")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

